'use client';

import React, { useEffect, useRef } from 'react';
import Image from 'next/image';
import { Siren, Pulse, FileText } from '@phosphor-icons/react';

const AUTH_ICONS = [
  {
    src: '/assets/onboarding/main-onboarding/auth-icons/github.svg',
    label: 'GitHub',
  },
  {
    src: '/assets/onboarding/main-onboarding/auth-icons/gitlab.svg',
    label: 'GitLab',
  },
  {
    src: '/assets/onboarding/main-onboarding/auth-icons/slack.svg',
    label: 'Slack',
  },
  {
    src: '/assets/onboarding/main-onboarding/auth-icons/discord.svg',
    label: 'Discord',
  },
  {
    src: '/assets/onboarding/main-onboarding/auth-icons/linear.svg',
    label: 'Linear',
  },
  {
    src: '/assets/onboarding/main-onboarding/auth-icons/jira.svg',
    label: 'Jira',
  },
  {
    src: '/assets/onboarding/main-onboarding/auth-icons/sentry.svg',
    label: 'Sentry',
  },
  {
    src: '/assets/onboarding/main-onboarding/auth-icons/cursor.svg',
    label: 'Cursor',
  },
  {
    src: '/assets/onboarding/main-onboarding/auth-icons/claude.svg',
    label: 'Claude',
  },
  {
    src: '/assets/onboarding/main-onboarding/auth-icons/betterstack.svg',
    label: 'Better Stack',
  },
];

function DottedGlowCanvas() {
  const canvasRef = useRef<HTMLCanvasElement>(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    const GAP = 12;
    const RADIUS = 1.5;
    const SPEED_MIN = 0.3;
    const SPEED_MAX = 1.0;
    const GLOW_THRESHOLD = 0.55;

    type Dot = { phase: number; speed: number };
    let dots: Dot[][] = [];
    let cols = 0;
    let rows = 0;
    let rafId: number;

    function resize() {
      const dpr = window.devicePixelRatio || 1;
      const w = canvas!.offsetWidth;
      const h = canvas!.offsetHeight;
      canvas!.width = w * dpr;
      canvas!.height = h * dpr;
      ctx!.setTransform(dpr, 0, 0, dpr, 0, 0);
      cols = Math.ceil(w / GAP) + 2;
      rows = Math.ceil(h / GAP) + 2;
      dots = Array.from({ length: rows }, () =>
        Array.from({ length: cols }, () => ({
          phase: Math.random() * Math.PI * 2,
          speed: SPEED_MIN + Math.random() * (SPEED_MAX - SPEED_MIN),
        }))
      );
    }

    let last = performance.now();
    const FRAME_BUDGET = 1000 / 30; // throttle to 30fps

    function draw(now: number) {
      rafId = requestAnimationFrame(draw);
      if (now - last < FRAME_BUDGET) return;
      const dt = Math.min((now - last) / 1000, 0.1);
      last = now;

      const w = canvas!.offsetWidth;
      const h = canvas!.offsetHeight;
      ctx!.clearRect(0, 0, w, h);

      // Pass 1: draw all normal dots (no shadow — cheap)
      ctx!.shadowBlur = 0;
      ctx!.shadowColor = 'transparent';
      for (let r = 0; r < rows; r++) {
        for (let c = 0; c < cols; c++) {
          const dot = dots[r][c];
          dot.phase += dot.speed * dt;
          const t = (dot.phase % (Math.PI * 2)) / (Math.PI * 2);
          const brightness = t < 0.5 ? t * 2 : (1 - t) * 2;
          const alpha = 0.05 + brightness * 0.15;
          ctx!.beginPath();
          ctx!.arc(c * GAP, r * GAP, RADIUS, 0, Math.PI * 2);
          ctx!.fillStyle = `rgba(160, 220, 80, ${alpha})`;
          ctx!.fill();
        }
      }

      // Pass 2: draw glowing dots on top (shadow set once per group)
      ctx!.shadowBlur = 10;
      for (let r = 0; r < rows; r++) {
        for (let c = 0; c < cols; c++) {
          const dot = dots[r][c];
          const t = (dot.phase % (Math.PI * 2)) / (Math.PI * 2);
          const brightness = t < 0.5 ? t * 2 : (1 - t) * 2;
          if (brightness <= GLOW_THRESHOLD) continue;
          const glowAlpha = (brightness - GLOW_THRESHOLD) * 3.0;
          ctx!.shadowColor = `rgba(180, 255, 100, ${glowAlpha})`;
          ctx!.beginPath();
          ctx!.arc(c * GAP, r * GAP, RADIUS, 0, Math.PI * 2);
          ctx!.fillStyle = `rgba(160, 220, 80, ${0.05 + brightness * 0.15})`;
          ctx!.fill();
        }
      }
      ctx!.shadowBlur = 0;
    }

    const ro = new ResizeObserver(resize);
    ro.observe(canvas);
    resize();
    rafId = requestAnimationFrame(draw); // draw() reschedules itself

    return () => {
      ro.disconnect();
      cancelAnimationFrame(rafId);
    };
  }, []);

  return (
    <canvas
      ref={canvasRef}
      className="absolute inset-0 z-0 w-full h-full pointer-events-none"
      style={{ opacity: 0.7 }}
      aria-hidden
    />
  );
}

export function AuthTestimonial() {
  return (
    <div
      className="auth-testimonial-panel hidden md:flex flex-col flex-1 relative overflow-hidden"
      style={{ background: '#2F3422' }}
    >
      {/* Layer 1: Dotted glow matrix (bottom) */}
      <DottedGlowCanvas />

      {/* Layer 2: Invert mask — smooth fade from center (transparent) to edges (dark), no hard cut (Figma node 1384-6024) */}
      <div
        aria-hidden
        className="absolute inset-0 z-[1] pointer-events-none"
        style={{
          background: '#2F3422',
          maskImage:
            'radial-gradient(ellipse 70% 80% at 50% 50%, transparent 0%, transparent 40%, black 75%, black 100%)',
          WebkitMaskImage:
            'radial-gradient(ellipse 70% 80% at 50% 50%, transparent 0%, transparent 40%, black 75%, black 100%)',
          maskSize: '100% 100%',
          maskPosition: 'center',
          maskRepeat: 'no-repeat',
          WebkitMaskSize: '100% 100%',
          WebkitMaskPosition: 'center',
          WebkitMaskRepeat: 'no-repeat',
        }}
      />
      {/* Layer 2b: Extra dark spots from Figma — bottom center, bottom right, top center, top right */}
      <div
        aria-hidden
        className="absolute inset-0 z-[1] pointer-events-none"
        style={{
          background: '#2F3422',
          opacity: 0.5,
          maskImage:
            'radial-gradient(ellipse 28% 18% at 50% 88%, black 0%, black 20%, transparent 65%, transparent 100%), radial-gradient(ellipse 22% 14% at 88% 85%, black 0%, black 20%, transparent 65%, transparent 100%), radial-gradient(ellipse 26% 16% at 50% 12%, black 0%, black 20%, transparent 65%, transparent 100%), radial-gradient(ellipse 20% 14% at 85% 15%, black 0%, black 20%, transparent 65%, transparent 100%)',
          WebkitMaskImage:
            'radial-gradient(ellipse 28% 18% at 50% 88%, black 0%, black 20%, transparent 65%, transparent 100%), radial-gradient(ellipse 22% 14% at 88% 85%, black 0%, black 20%, transparent 65%, transparent 100%), radial-gradient(ellipse 26% 16% at 50% 12%, black 0%, black 20%, transparent 65%, transparent 100%), radial-gradient(ellipse 20% 14% at 85% 15%, black 0%, black 20%, transparent 65%, transparent 100%)',
          maskSize: '100% 100%',
          maskPosition: 'center',
          maskRepeat: 'no-repeat',
          maskComposite: 'add',
          WebkitMaskSize: '100% 100%',
          WebkitMaskPosition: 'center',
          WebkitMaskRepeat: 'no-repeat',
        }}
      />
      {/* Layer 2c: Center mask — visible dark ellipse behind testimonial */}
      <div
        aria-hidden
        className="absolute inset-0 z-[1] pointer-events-none"
        style={{
          background: '#303524',
          opacity: 0.78,
          maskImage:
            'radial-gradient(ellipse 78% 82% at 50% 50%, black 0%, black 22%, transparent 50%, transparent 100%)',
          WebkitMaskImage:
            'radial-gradient(ellipse 78% 82% at 50% 50%, black 0%, black 22%, transparent 50%, transparent 100%)',
          maskSize: '100% 100%',
          maskPosition: 'center',
          maskRepeat: 'no-repeat',
          WebkitMaskSize: '100% 100%',
          WebkitMaskPosition: 'center',
          WebkitMaskRepeat: 'no-repeat',
        }}
      />

      {/* Layer 3: Content — pills, testimonial, icon strip (above invert mask) */}
      <>
        {/* Top-left: Error spike detected — position and size scale with screen */}
        <div className="absolute top-[6%] sm:top-[7%] lg:top-[8%] left-[3%] sm:left-[4%] lg:left-[5%] z-10 flex items-center gap-[6px] px-[12px] py-[8px] rounded-full border-[1.433px] border-[#efdbc0] bg-[#fdf3e6] shadow-[0px_3.333px_12.582px_0px_rgba(106,114,82,0.08)]">
          <Siren
            size={16}
            weight="regular"
            className="text-[#b07030] shrink-0"
          />
          <span className="text-[#4f402d] text-[15px] font-medium leading-[24px] tracking-[-0.325px] whitespace-nowrap">
            Error spike detected
          </span>
        </div>

        {/* Top-right: Next up per engineer — position and size scale with screen */}
        <div className="absolute top-[20%] sm:top-[22%] lg:top-[24%] right-[3%] sm:right-[4%] lg:right-[5%] z-20 flex items-center gap-[6px] px-[12px] py-[8px] rounded-full border-[1.433px] border-[rgba(71,88,47,0.12)] bg-[#f2f5ec] shadow-[0px_3.333px_12.582px_0px_rgba(106,114,82,0.08)]">
          <Pulse
            size={20}
            weight="regular"
            className="text-[#47582f] shrink-0"
          />
          <span className="text-[#47582f] text-[15px] font-medium leading-[24px] tracking-[-0.325px] whitespace-nowrap">
            Next up per engineer
          </span>
        </div>

        {/* Mid-right: PRs merged & blocked — position and size scale with screen */}
        <div className="absolute top-[52%] sm:top-[55%] lg:top-[58%] right-[2.5%] sm:right-[3%] lg:right-[4%] z-10 flex items-center gap-[6px] px-[12px] py-[8px] rounded-full border-[1.433px] border-[rgba(71,88,47,0.12)] bg-[#f2f5ec] shadow-[0px_3.333px_12.582px_0px_rgba(106,114,82,0.08)]">
          <Pulse
            size={20}
            weight="regular"
            className="text-[#47582f] shrink-0"
          />
          <span className="text-[#47582f] text-[15px] font-medium leading-[24px] tracking-[-0.325px] whitespace-nowrap">
            PRs merged &amp; blocked
          </span>
        </div>

        {/* Bottom-left: Sprint health summary — position and size scale with screen */}
        <div className="absolute bottom-[12%] sm:bottom-[14%] lg:bottom-[16%] left-[3%] sm:left-[4%] lg:left-[5%] z-10 flex items-center gap-[6px] px-[12px] py-[8px] rounded-full border-[1.433px] border-[#c8c2f5] bg-[#efedfd] shadow-[0px_3.333px_12.582px_0px_rgba(106,114,82,0.08)]">
          <FileText
            size={16}
            weight="regular"
            className="text-[#5d539f] shrink-0 opacity-75"
          />
          <span className="text-[#5d539f] text-[15px] font-medium leading-[24px] tracking-[-0.325px] whitespace-nowrap">
            Sprint health summary
          </span>
        </div>

        {/* Main testimonial content — Figma 1372-5317: quote + circular avatar, name, smaller/lighter title */}
        <div className="relative z-10 flex flex-col justify-center flex-1">
          <div
            className="flex flex-col gap-[40px]"
            style={{
              width: 520,
              marginLeft: 'auto',
              marginRight: 'auto',
              position: 'relative',
            }}
          >
            {/* Quote — light desaturated green; key phrases bold + light grey wavy underline */}
            <p
              className="font-instrument-sans font-medium text-[20px] leading-[1.6] tracking-[0.01em] align-middle whitespace-pre-wrap"
              style={{ color: 'rgba(231, 242, 212, 0.65)' }}
            >
              <span>{'Entelligence '}</span>
              <span
                className="font-semibold"
                style={{
                  color: 'var(--brand-subtlest, #E7F2D4)',
                  textDecoration: 'underline',
                  textDecorationStyle: 'wavy',
                  textDecorationColor: 'rgba(214, 224, 190, 0.6)',
                  textUnderlineOffset: '5px',
                  textDecorationThickness: '1.25px',
                }}
              >
                caught bugs that had silently shipped
              </span>
              <span>
                {
                  ' before race conditions, unclosed resources, validation misses. The AI comments felt '
                }
              </span>
              <span
                className="font-semibold"
                style={{
                  color: 'var(--brand-subtlest, #E7F2D4)',
                  textDecoration: 'underline',
                  textDecorationStyle: 'wavy',
                  textDecorationColor: 'rgba(214, 224, 190, 0.6)',
                  textUnderlineOffset: '5px',
                  textDecorationThickness: '1.25px',
                }}
              >
                actionable &amp; not noisy.
              </span>
            </p>

            {/* Attribution — circular avatar, name same light as quote, title slightly smaller and lighter */}
            <div className="flex items-start gap-[12px]">
              <div className="relative w-[50px] h-[50px] rounded-lg shrink-0 overflow-hidden border border-ds-brand-subtle">
                <Image
                  src="/assets/onboarding/main-onboarding/michael.png"
                  alt="Michael Clark"
                  fill
                  sizes="50px"
                  className="object-cover"
                  preload
                />
              </div>
              <div className="flex flex-col gap-[2px] justify-center">
                <p
                  className="font-medium text-[16px] leading-[1.5] tracking-[-0.16px]"
                  style={{ color: 'rgba(231, 242, 212, 0.9)' }}
                >
                  Michael Clark
                </p>
                <p
                  className="font-semibold text-[14px] leading-[1.5] tracking-[-0.14px]"
                  style={{ color: 'rgba(193, 206, 158, 0.85)' }}
                >
                  Director of Engineering, Allocore
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Bottom icon layer — icons from auth-icons folder, positioned lower so they’re clipped by the panel edge */}
        <div
          className="absolute inset-x-0 bottom-[-18px] z-[2] flex w-full items-center justify-evenly pointer-events-none opacity-20 px-2 gap-[clamp(0.5rem,1.5vw,1.5rem)]"
          aria-hidden
        >
          {AUTH_ICONS.map(({ src, label }) => (
            <div
              key={label}
              className="shrink-0 w-9 h-9 sm:w-10 sm:h-10 md:w-11 md:h-11 lg:w-12 lg:h-12 xl:w-14 xl:h-14"
            >
              <Image
                src={src}
                alt={label}
                width={72}
                height={72}
                className="object-contain w-full h-full"
                unoptimized
              />
            </div>
          ))}
        </div>
      </>
    </div>
  );
}
